// Active Parking Journey card. One primary action at a time, nothing permanent:
// it only exists while a journey is running.

import React from "react";
import { motion } from "framer-motion";
import { Navigation, Zap, MapPin, Check, X, ArrowRight, Car, Footprints, ChevronDown, ChevronUp } from "lucide-react";
import { formatWalk, preferredNavApp, rememberNavApp } from "../lib/journey";
import { accessibleLabel, accessibleSource } from "../lib/mapLayers";

const Card = ({ children, testId }) => {
  const ref = React.useRef(null);
  const [collapsed, setCollapsed] = React.useState(false);
  // Publish the card height so the floating map controls can sit above it
  // instead of disappearing behind it on short phone screens.
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty("--journey-card-h", `${Math.round(el.offsetHeight)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty("--journey-card-h", "0px");
    };
  }, []);
  return (
    <motion.div
      ref={ref}
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="fixed inset-x-2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:w-[420px] z-[1500] rounded-3xl bg-[#141416]/95 border border-white/10 backdrop-blur p-3.5 shadow-[0_18px_50px_rgba(0,0,0,0.6)]"
      style={{ bottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full -mt-1 mb-1 py-1 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500 active:text-zinc-300"
        data-testid="journey-card-collapse"
        aria-expanded={!collapsed}
        aria-label={collapsed ? "Show journey options" : "Hide journey options to see the map"}
      >
        <span className="w-8 h-1 rounded-full bg-white/20" />
        {collapsed ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {collapsed ? (
        <div className="pb-0.5 text-[12px] text-zinc-400 text-center" data-testid="journey-card-collapsed">
          Journey running — tap to show options
        </div>
      ) : (
        children
      )}
    </motion.div>
  );
};

const Head = ({ option, destination }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
        option.source === "live"
          ? "bg-[#ccff00]/15 border border-[#ccff00]/40"
          : "bg-white/5 border border-white/10"
      }`}
    >
      {option.source === "live" ? (
        <Zap className="w-4 h-4 text-[#ccff00]" />
      ) : (
        <MapPin className="w-4 h-4 text-zinc-300" />
      )}
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] font-semibold text-white truncate" data-testid="journey-option-type">
        {option.type_label}
      </div>
      <div className="text-[12px] text-zinc-400 mt-0.5">
        {formatWalk(option)}
        {destination ? ` to ${destination.name}` : ""}
      </div>
      <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{option.status}</div>
      {option.accessible?.has_bays && (
        <div className="text-[11px] text-blue-300 mt-0.5 truncate" data-testid="journey-accessible">
          ♿ {accessibleLabel(option.accessible)} · {accessibleSource(option.accessible)}
        </div>
      )}
    </div>
  </div>
);

export default function JourneyCard({
  journey,
  onDirections,
  onFound,
  onFailed,
  onTryAnother,
  onGoThere,
  onSaveParking,
  onWalkToDestination,
  onDone,
}) {
  const [chooser, setChooser] = React.useState(false);
  if (!journey) return null;
  const { selected, destination, pending, status } = journey;
  const pref = preferredNavApp();
  const go = (app) => {
    rememberNavApp(app);
    setChooser(false);
    onDirections(app);
  };

  if (status === "parked") {
    return (
      <Card testId="journey-parked-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#ccff00]/15 border border-[#ccff00]/40 flex items-center justify-center shrink-0">
            <Check className="w-4 h-4 text-[#ccff00]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-white">Parked. Nice one.</div>
            <div className="text-[12px] text-zinc-400 mt-0.5 truncate">
              {destination ? `${destination.name} is ${formatWalk(selected)}` : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={onDone}
            className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-zinc-500"
            aria-label="Close journey"
            data-testid="journey-done"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={onSaveParking}
          className="mt-3 w-full h-11 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px] inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="journey-save-parking"
        >
          <Car className="w-4 h-4" /> Save parking
        </button>
        <button
          type="button"
          onClick={onWalkToDestination}
          className="mt-2 w-full h-11 rounded-full bg-[#0a0a0c] border border-white/15 text-white text-[13px] inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="journey-walk-btn"
        >
          <Footprints className="w-4 h-4" /> Walk to {destination?.name || "destination"}
        </button>
      </Card>
    );
  }

  if (status === "next" && pending) {
    return (
      <Card testId="journey-next-card">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#ccff00] font-semibold mb-2">
          Next best option
        </div>
        <Head option={pending} destination={destination} />
        <button
          type="button"
          onClick={onGoThere}
          className="mt-3 w-full h-11 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px] inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="journey-go-there"
        >
          Go there <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onDone}
          className="mt-1.5 w-full h-9 text-[12px] text-zinc-500"
          data-testid="journey-end"
        >
          End journey
        </button>
      </Card>
    );
  }

  if (status === "asking") {
    return (
      <Card testId="journey-ask-card">
        <div className="text-[13px] font-semibold text-white">Did you find parking?</div>
        <div className="text-[12px] text-zinc-500 mt-0.5 truncate">{selected.type_label}</div>
        <button
          type="button"
          onClick={onFound}
          className="mt-3 w-full h-11 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px] active:scale-[0.98] transition-transform"
          data-testid="journey-found-btn"
        >
          Yes, parked
        </button>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={onFailed}
            className="flex-1 h-11 rounded-full bg-[#0a0a0c] border border-white/15 text-white text-[12px] active:scale-[0.98] transition-transform"
            data-testid="journey-failed-btn"
          >
            No, occupied
          </button>
          <button
            type="button"
            onClick={onTryAnother}
            className="flex-1 h-11 rounded-full bg-[#0a0a0c] border border-white/15 text-zinc-300 text-[12px] active:scale-[0.98] transition-transform"
            data-testid="journey-try-another-btn"
          >
            Try another
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card testId="journey-card">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <Head option={selected} destination={destination} />
        </div>
        <button
          type="button"
          onClick={onDone}
          className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-zinc-500"
          aria-label="End journey"
          data-testid="journey-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {chooser ? (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="nav-chooser">
          <button
            type="button"
            onClick={() => go("google")}
            className="h-11 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[12px] active:scale-[0.98] transition-transform"
            data-testid="nav-google-btn"
          >
            Google Maps
          </button>
          <button
            type="button"
            onClick={() => go("waze")}
            className="h-11 rounded-full bg-[#0a0a0c] border border-white/20 text-white font-semibold text-[12px] active:scale-[0.98] transition-transform"
            data-testid="nav-waze-btn"
          >
            Waze
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => (pref ? onDirections(pref) : setChooser(true))}
          className="mt-3 w-full h-11 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-[13px] inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
          data-testid="journey-directions-btn"
        >
          <Navigation className="w-4 h-4" /> Directions
        </button>
      )}
      <div className="mt-1.5 flex items-center justify-center gap-4 h-9">
        {status === "navigating" && (
          <button
            type="button"
            onClick={onFound}
            className="text-[12px] text-[#ccff00] font-semibold"
            data-testid="journey-parked-link"
          >
            I parked
          </button>
        )}
        <button
          type="button"
          onClick={onTryAnother}
          className="text-[12px] text-zinc-400"
          data-testid="journey-try-another-link"
        >
          Try another option
        </button>
        {pref && !chooser && (
          <button
            type="button"
            onClick={() => setChooser(true)}
            className="text-[12px] text-zinc-600"
            data-testid="nav-change-btn"
          >
            Change app
          </button>
        )}
      </div>
    </Card>
  );
}
